import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { contextService, UserContext } from "@/domain/services/contextService";
import { useAuth } from "@/application/services/authService";

interface UserContextProviderState {
  context: UserContext;
  isLoading: boolean;
  error: string | null;
  refreshContext: () => Promise<UserContext>;
  updateContext: (newContext: Partial<UserContext>) => Promise<UserContext>;
}

const UserContextProviderContext = createContext<UserContextProviderState>({
  context: {},
  isLoading: false,
  error: null,
  refreshContext: async () => ({}),
  updateContext: async () => ({}),
});

interface UserContextProviderProps {
  children: ReactNode;
  refreshInterval?: number; // 自動刷新間隔（毫秒），如果不需要自動刷新，可以設為 0 或不傳
}

/**
 * 用戶上下文提供者組件
 * 管理用戶上下文的獲取、更新和刷新
 */
export function UserContextProvider({
  children,
  refreshInterval = 0, // 預設不自動刷新
}: UserContextProviderProps) {
  const { isAuthenticated } = useAuth();
  const [state, setState] = useState<{
    context: UserContext;
    isLoading: boolean;
    error: string | null;
  }>({
    context: {},
    isLoading: true,
    error: null,
  });

  // 刷新用戶上下文
  const refreshContext = async (): Promise<UserContext> => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      // 從服務器獲取最新的用戶上下文
      const freshContext = await contextService.getCurrentContext();

      // 如果獲取成功，更新本地儲存和狀態
      if (freshContext && Object.keys(freshContext).length > 0) {
        contextService.saveLocalContext(freshContext);
        setState({
          context: freshContext,
          isLoading: false,
          error: null,
        });
        return freshContext;
      } else {
        // 如果服務器返回空上下文，嘗試使用本地儲存的上下文
        const localContext = contextService.getLocalContext();
        setState({
          context: localContext,
          isLoading: false,
          error: null,
        });
        return localContext;
      }
    } catch (error) {
      console.error("Failed to refresh user context:", error);

      // 發生錯誤時，嘗試使用本地儲存的上下文
      const localContext = contextService.getLocalContext();
      setState({
        context: localContext,
        isLoading: false,
        error: "無法獲取用戶上下文",
      });
      return localContext;
    }
  };

  // 更新用戶上下文
  const updateContext = async (
    newContext: Partial<UserContext>,
  ): Promise<UserContext> => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      // 調用 API 更新用戶上下文
      const updatedContext = await contextService.setCurrentContext(newContext);

      // 更新本地儲存和狀態
      contextService.saveLocalContext(updatedContext);
      setState({
        context: updatedContext,
        isLoading: false,
        error: null,
      });

      return updatedContext;
    } catch (error) {
      console.error("Failed to update user context:", error);

      // 發生錯誤時，返回當前狀態的上下文
      const currentContext = state.context;
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: "無法更新用戶上下文",
      }));

      return currentContext;
    }
  };

  // 在組件初始化時以及 isAuthenticated 變化時獲取用戶上下文
  useEffect(() => {
    // 只在已經登入的狀態下才自動獲取上下文
    if (isAuthenticated) {
      console.log("用戶已登入，準備獲取上下文數據");
      // 無論是否已有上下文數據，都嘗試刷新一次
      // 這樣可以確保登入後能獲取最新的上下文
      refreshContext();
    } else {
      // 未登入時，清空上下文
      setState({
        context: {},
        isLoading: false,
        error: null,
      });
    }
  }, [isAuthenticated]); // 當認證狀態變化時重新執行

  // 如果設置了刷新間隔，定期刷新用戶上下文
  useEffect(() => {
    if (!refreshInterval || refreshInterval <= 0 || !isAuthenticated) return;

    const intervalId = setInterval(refreshContext, refreshInterval);
    return () => clearInterval(intervalId);
  }, [refreshInterval, isAuthenticated]);

  return (
    <UserContextProviderContext.Provider
      value={{
        ...state,
        refreshContext,
        updateContext,
      }}
    >
      {children}
    </UserContextProviderContext.Provider>
  );
}

/**
 * 使用用戶上下文的自定義 Hook
 */
export function useUserContext() {
  return useContext(UserContextProviderContext);
}
